import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Menu as BaseMenu,
  MenuButton as BaseMenuButton,
  MenuItem as BaseMenuItem,
  MenuItems as BaseMenuItems,
  Portal,
} from '@headlessui/react';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useRef, useState } from 'react';

type MenuProps = {
  children?: React.ReactNode;
  buttonClassName?: string;
  buttonText?: React.ReactNode;
  topPosition?: string;
  usePortal?: boolean;
};

const DEFAULT_BUTTON_CONTENT = (
  <FontAwesomeIcon
    icon={faEllipsisVertical as IconProp}
    className='h-5 w-5 hover:text-brand'
  />
);

export function Menu({
  children,
  topPosition,
  buttonClassName,
  buttonText = DEFAULT_BUTTON_CONTENT,
  usePortal = true,
}: MenuProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [portalPosition, setPortalPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const handleOpenFromButton = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Position the menu anchored to the button's top-right, with slight vertical offset
    setPortalPosition({
      top: Math.round(rect.bottom + 12),
      right: Math.round(window.innerWidth - rect.right),
    });
  };

  return (
    <BaseMenu as='div' className='relative flex text-left'>
      {({ open }) => (
        <>
          <BaseMenuButton
            ref={buttonRef}
            className={
              buttonClassName ??
              classNames(
                'duration-250 flex h-10 w-10 items-center justify-center rounded-full text-secondary transition-all hover:bg-gray-100 hover:text-primary active:bg-gray-200',
                {
                  'bg-gray-100 text-primary': open,
                }
              )
            }
            onClick={handleOpenFromButton}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleOpenFromButton();
              }
            }}
          >
            {buttonText}
          </BaseMenuButton>
          <AnimatePresence>
            {open &&
              (usePortal ? (
                <Portal>
                  <motion.div
                    className='fixed z-[1001]'
                    style={{
                      top: portalPosition?.top ?? 0,
                      right: portalPosition?.right ?? 0,
                    }}
                    initial={{
                      opacity: 0,
                      scale: 0.8,
                      transformOrigin: 'top right',
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      transformOrigin: 'top right',
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.8,
                      transformOrigin: 'top right',
                    }}
                    transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <BaseMenuItems className='z-[1001] w-44 rounded-md border border-gray55 bg-white shadow-lg focus:outline-none'>
                      {children}
                    </BaseMenuItems>
                  </motion.div>
                </Portal>
              ) : (
                <motion.div
                  className={classNames(
                    'absolute right-0 z-[1000] mt-3',
                    topPosition ?? 'top-8'
                  )}
                  initial={{
                    opacity: 0,
                    scale: 0.8,
                    transformOrigin: 'top right',
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    transformOrigin: 'top right',
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.8,
                    transformOrigin: 'top right',
                  }}
                  transition={{
                    duration: 0.15,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  <BaseMenuItems className='z-[1001] w-44 rounded-md border border-gray55 bg-white shadow-lg focus:outline-none'>
                    {children}
                  </BaseMenuItems>
                </motion.div>
              ))}
          </AnimatePresence>
        </>
      )}
    </BaseMenu>
  );
}

function Item(props: Parameters<typeof BaseMenuItem>[0]) {
  return (
    <BaseMenuItem
      as={props.as ?? 'div'}
      className='block min-w-full cursor-pointer border-b border-outline-variant px-4 py-5 text-left text-sm font-medium text-copy-body last:border-b-0 hover:text-brand'
      {...props}
    />
  );
}

Menu.Item = Item;
